import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { Getter, Setter } from 'jotai/index';

import { CaregiverCallConfirmationType } from '@/shared/call/CaregiverCallConfirmationType';
import { assertExhausted } from '@/shared/enums';
import { FetchError } from '@/shared/fetch';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { fetchConfirmationData } from '../api/fetchConfirmationData';
import { initiateVideoCall } from '../api/initiateVideoCall';
import { PhoneCallHandler } from '../lib/PhoneCallHandler';
import { CallStartError } from './CallStartError';
import { CallStatus } from './CallStatus';
import { ConfirmationDto } from './ConfirmationDto';

const CAREGIVER_CALL_CONFIRMATION_KEY = 'CAREGIVER_CALL_CONFIRMATION';

export const consultationUidAtom = atom<string>();
export const internalOnCloseAtom = atom<() => void>();
export const confirmationTypeAtom = atom<CaregiverCallConfirmationType>();
export const callStatusAtom = atom<CallStatus>();
export const isConnectingAtom = atom(false);

export const isHandledByOtherAtom = atom(
    (get) => get(confirmationAtom).IsConsultationHandledByOtherCaregiver,
    (get, set, isHandledByOther: boolean) => {
        const confirmation = get(confirmationAtom);
        set(confirmationAtom, {
            ...confirmation,
            IsConsultationHandledByOtherCaregiver: isHandledByOther,
        });
    },
);

export const confirmationAtom = atom<ConfirmationDto>();

export const phoneCallHandlerAtom = atom<PhoneCallHandler>();

export const caregiverCallConfirmationQueryAtom = atomWithQuery<
    ConfirmationDto,
    FetchError<ConfirmationDto>
>((get) => {
    const consultationUid = get(consultationUidAtom);
    const type = get(confirmationTypeAtom);

    return {
        queryKey: [CAREGIVER_CALL_CONFIRMATION_KEY, consultationUid, type],
        queryFn: async () => {
            if (!consultationUid || !type) {
                throw new Error('Invalid consultation ID or type');
            }

            return await fetchConfirmationData({
                consultationUid,
                type,
            });
        },
    };
});

export const makeCallAtom = atom(null, async (get, set) => {
    const consultationUid = get(consultationUidAtom);
    const confirmationType = get(confirmationTypeAtom);
    const callStatus = get(callStatusAtom);
    const confirmation = get(confirmationAtom);

    set(isConnectingAtom, true);

    switch (confirmationType) {
        case CaregiverCallConfirmationType.video: {
            await handleVideoCall(consultationUid, confirmation, set);
            break;
        }
        case CaregiverCallConfirmationType.phone: {
            await handlePhoneCall(confirmation, callStatus, get, set);
            break;
        }
        default:
            assertExhausted(confirmationType);
            break;
    }
});

const handleVideoCall = async (
    consultationUid: string,
    confirmation: ConfirmationDto,
    set: Setter,
) => {
    const error = await initiateVideoCall(
        consultationUid,
        confirmation.IsConsultationHandledByOtherCaregiver,
    );
    set(updateCallStatusAtom, error);
};

const handlePhoneCall = async (
    confirmation: ConfirmationDto,
    callStatus: CallStatus,
    get: Getter,
    set: Setter,
) => {
    if (callStatus === CallStatus.notStarted) {
        set(callStatusAtom, CallStatus.ongoing);
        const phoneCallHandler = get(phoneCallHandlerAtom);
        const error = await phoneCallHandler.makeCall(
            confirmation.IsConsultationHandledByOtherCaregiver,
        );
        set(updateCallStatusAtom, error);
    }
};

const updateCallStatusAtom = atom(
    null,
    (get, set, error: CallStartError | null) => {
        const callStatus = get(callStatusAtom);

        if (error === null) {
            return;
        }

        if (callStatus === CallStatus.ongoing) {
            set(callStatusAtom, CallStatus.notStarted);
        }

        switch (error) {
            case CallStartError.isHandledByOther:
                set(isHandledByOtherAtom, true);
                set(isConnectingAtom, false);
                break;
            case CallStartError.unknown:
                window.alert(R.UnknownErrorMessage);
                set(isConnectingAtom, false);
                break;
            default:
                assertExhausted(error);
        }
    },
);

export const shutdownConfirmationAtom = atom(null, (get, set) => {
    const phoneCallHandler = get(phoneCallHandlerAtom);
    void phoneCallHandler?.viewClosed();
    set(phoneCallHandlerAtom, null);
    set(isConnectingAtom, false);
    set(callStatusAtom, CallStatus.notStarted);
});

export const hangUpPhoneCallAtom = atom(null, (get, set) => {
    const phoneCallHandler = get(phoneCallHandlerAtom);
    void phoneCallHandler?.hangUp();
    set(callStatusAtom, CallStatus.notStarted);
});
